/**
 * Phobs API
 */
const factory = function factory({ window, document, thn }) {
    let refreshPrice = null;
    const calculatePrice = (price, guests) => {
        let priceCalculated = 0;
        const totalGuests = guests.adults + guests.children;
        priceCalculated = price * guests.adults;
        const calculateChildPrice = ((age) => {
            if (totalGuests <= 3 && age >= 18)
                return price * 0.8;
            if ((age < 12 && age > 7) || age < 7)
                return price * 0.5;
            if (age >= 12 && age < 18)
                return price * 0.7;
            return price;
        });
        const childrenAges = [...guests.childrenAges];
        if (childrenAges.filter(age => age < 7).length > 0) {
            const indexAge = childrenAges.findIndex((age) => age < 7);
            childrenAges.splice(indexAge, 1);
        }
        childrenAges.forEach((age) => {
            priceCalculated += calculateChildPrice(age);
        });
        return priceCalculated;
    };
    let updatePageOnDataLayerEvent = null;
    return {
        boot() {
            updatePageOnDataLayerEvent = (data) => {
                if (!data.message)
                    return;
                const message = data.message;
                if (message.event === 'gtm.historyChange-v2' || message.event === 'search' || message.search_term) {
                    thn.languageCheck().catch(() => { });
                    thn.pageCheck();
                }
            };
            thn.bus.on('dataLayer:push', updatePageOnDataLayerEvent, true);
            thn.shutdown(() => {
                if (updatePageOnDataLayerEvent) {
                    thn.bus.detach('dataLayer:push', updatePageOnDataLayerEvent);
                }
                updatePageOnDataLayerEvent = null;
            });
        },
        generateSearchUrl(ctx) {
            const url = new URL(ctx.baseUrl);
            const validUrl = url.pathname.includes('booking');
            // https://ilirijabiograd.com/en/booking?hotel=hotel-ilirija&checkin=2024-07-21&nights=3&adults=2&rate=&promo=
            if (!validUrl) {
                ctx.newUrl = '';
                return ctx;
            }
            ctx.newUrl = url.toString();
            return ctx;
        },
        onStateChange() {
            thn.debounce(thn.pageCheck, 400)();
        },
        priceObserveDom(ctx) {
            const container = document.querySelector('[class*="RoomList_hotel"]');
            const loader = document.querySelector('.animate-skeletonPulse');
            if (container && !refreshPrice) {
                refreshPrice = thn.debounce(() => {
                    if (!thn.isVisible(loader)) {
                        ctx.widget.refresh().catch(() => { });
                    }
                }, 400);
                thn.observeDom(container, refreshPrice, {
                    attributes: true,
                });
                thn.pageUnload(() => {
                    if (refreshPrice) {
                        refreshPrice.cancel();
                        thn.stopObserveDom(container, refreshPrice);
                        refreshPrice = null;
                    }
                });
            }
            // hide price when making a new search to avoid showing old prices
            const updatePriceOnDataLayerEvent = thn.debounce(() => {
                if (ctx.widget.isSet()) {
                    ctx.widget.update().catch(() => { });
                }
            }, 500);
            thn.bus.on('dataLayer:event.search', updatePriceOnDataLayerEvent, true);
            thn.bus.on('dataLayer:event.gtm.historyChange-v2', updatePriceOnDataLayerEvent, true);
            thn.pageUnload(() => {
                thn.bus.detach('dataLayer:event.gtm.historyChange-v2', updatePriceOnDataLayerEvent);
                thn.bus.detach('dataLayer:event.search', updatePriceOnDataLayerEvent);
            });
        },
        priceParseData() {
            // Guests, Adults & Children
            const url = new URL(window.location.toString());
            const childrenAgesParam = url.searchParams.get('children')?.split(',') ?? [];
            const childrenAges = childrenAgesParam.map(age => thn.parseNumber(age)).filter((age) => age !== undefined) ?? [];
            const numAdults = thn.parseNumber(thn.parseUrlParameter('adults'));
            const roomInfo = {
                adults: numAdults ?? 2,
                children: childrenAges.length,
                childrenAges,
            };
            // Dates
            const dateBeginToday = thn.dateToString(new Date()); // when we don't have params, the hotel shows the current date
            const dateEndTomorrow = thn.dateToString(thn.addDays(new Date(), 1)); // when we don't have params, the hotel shows the next day
            const dateBegin = (thn.parseUrlParameter('checkin')?.replace(/-/g, '/')) || dateBeginToday || '';
            const nights = thn.parseNumber(thn.parseUrlParameter('nights')) ?? 0;
            const dateEnd = (nights && thn.dateToString(thn.addDays(dateBegin, nights))) || dateEndTomorrow || '';
            if (!dateBegin && !dateEnd) {
                return null;
            }
            // Price and Currency
            const queryResults = document.evaluate('//span[contains(text(),"€")]', document, null, XPathResult.ORDERED_NODE_SNAPSHOT_TYPE, null);
            const prices = [];
            for (let i = 0; i < queryResults.snapshotLength; i += 1) {
                const el = queryResults.snapshotItem(i);
                const clonedPrice = el?.parentNode?.cloneNode(true);
                clonedPrice.querySelector('s')?.remove(); // Remove price with underline
                const parsedPrice = thn.parseNumber(clonedPrice?.textContent);
                if (parsedPrice !== undefined) {
                    prices.push(parsedPrice);
                }
            }
            const basePrice = Math.min(...prices);
            const price = thn.data.accountUserId === 1047378 ? calculatePrice(basePrice, roomInfo) : basePrice / nights;
            const currency = 'EUR';
            const noAvailability = thn.empty(prices);
            if (!thn.empty(prices) && currency) {
                return {
                    dateBegin,
                    dateEnd,
                    roomsDispo: [roomInfo],
                    price,
                    currency,
                };
            }
            if (noAvailability) {
                return {
                    dateBegin,
                    dateEnd,
                    roomsDispo: [roomInfo],
                    noRooms: true,
                };
            }
            return null;
        },
        bookingParseData() {
            const bookingId = thn.dataLayerHelper.get('ecommerce.transaction_id') || thn.dataLayerHelper.get('transaction_id')
                || thn.parseUrlParameter('reservationId') || thn.parseUrlParameter('reservation-id');
            if (!bookingId) {
                return {
                    set: false,
                };
            }
            const dateBegin = (thn.dataLayerHelper.get('ecommerce.purchase.products.0.checkIn') ?? thn.parseUrlParameter('checkin'))?.replace(/-/g, '/');
            const dateEnd = (thn.dataLayerHelper.get('ecommerce.purchase.products.0.checkOut') ?? thn.parseUrlParameter('checkout'))?.replace(/-/g, '/');
            const price = thn.dataLayerHelper.get('ecommerce.value');
            const currency = thn.dataLayerHelper.get('ecommerce.currency');
            return {
                bookingId,
                dateBegin,
                dateEnd,
                price,
                currency,
            };
        },
    };
};

export { factory };
